import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';
import dateService from '@gem/services/date-service';

import Tasks from 'APP/Tasks';
import { setDeviceStatus } from 'APP/model/userSettings/userSettingsModel';
import { DeviceStatus, IUserDevicesItem } from 'APP/model/userSettings/userSettingsModel.types';
import Entities from 'STORE';

interface ITrustedDeviceRequestPopupPresenter {
  onClose(): void;
  onBack(): void;
  onApprove(): Promise<void>;
  onBlock(): Promise<void>;
  session: IUserDevicesItem | null;
  deviceName: string;
  deviceOS: string;
  isLoading: boolean;
  activity: string | null;
  deviceId: string;
}

interface ITrustedDeviceRequestPopupPresenterProps {
  deviceId: string;
}

export function useTrustedDeviceRequestPopupPresenter({
  popupInstance,
  onClose,
  onBack,
  params,
}: ICommonPopupsProps<ITrustedDeviceRequestPopupPresenterProps>): ITrustedDeviceRequestPopupPresenter {
  const { t } = useTranslation();
  const presenter = useLocalStore<ITrustedDeviceRequestPopupPresenter>(() => ({
    deviceId: params!.deviceId,
    isLoading: false,

    get session(): IUserDevicesItem | null {
      return Entities.sessionsStore.getSessionByKey(params!.deviceId);
    },

    get deviceOS(): string {
      const filteredDeviceDetails = [
        presenter.session?.device.osName || '',
        presenter.session?.device.osVersion || '',
      ].filter(Boolean);
      if (!filteredDeviceDetails.length) {
        return '';
      }
      return filteredDeviceDetails.join(' ');
    },

    get activity(): string | null {
      if (!presenter.session?.device.statusAt) {
        return null;
      }

      const sessionDate = new Date(Number(presenter.session.device.statusAt)).getTime();
      return (
        dateService.format(sessionDate, {
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        }) || ''
      );
    },

    get deviceName(): string {
      return presenter.session?.device.device || '';
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onBack(): void {
      popupInstance.close();
      onBack?.();
    },

    async onApprove(): Promise<void> {
      presenter.isLoading = true;
      try {
        await Tasks.e2ee.approveTrustedDevice(params!.deviceId);
        Tasks.app.showToast(t('trusted_device_access_confirmed'));
        presenter.onClose();
      } catch (e) {
        Tasks.app.showToast(t('something_went_wrong'));
      } finally {
        presenter.isLoading = false;
      }
    },

    async onBlock(): Promise<void> {
      const result = await Tasks.app.showConfirm({
        title: t('trusted_device_block_device_title'),
        text: t('trusted_device_block_device_description'),
        primaryText: t('common_block'),
      });

      if (!result) {
        return;
      }

      presenter.isLoading = true;
      try {
        await setDeviceStatus({ deviceId: params!.deviceId, status: DeviceStatus.Blocked });
        Tasks.app.showToast(t('trusted_device_sign_in_request_blocked'));
        presenter.onClose();
      } catch (e) {
        Tasks.app.showToast(t('something_went_wrong'));
      } finally {
        presenter.isLoading = false;
      }
    },
  }));

  useEffect(() => {
    presenter.deviceId = params!.deviceId;
  }, [params!.deviceId]);

  useEffect(() => {
    if (presenter.session?.device.status !== DeviceStatus.Pending) {
      presenter.onClose();
    }
  }, [presenter.session?.device.status]);

  return presenter;
}
